import { z } from 'zod';

const setTemplateDto = z.object({
  channel: z.string(),
  distance: z.number().nullable(),
  frequency: z.number(),
  huDelays: z.array(z.number()),
  includes: z.number(),
  isTemplate: z.boolean(),
  label: z.string(),
  markers: z.record(z.string(), z.array(z.number())),
  numBeats: z.number(),
  inNeg: z.string().nullable(),
  inPos: z.string().nullable(),
  outNeg: z.string().nullable(),
  outPos: z.string().nullable(),
  quality: z.number().nullable(),
  smpRate: z.number(),
  startValues: z.number(),
  type: z.string().nullable(),
  values: z.object({
    checksum: z.number(),
    dataBase64: z.string().transform((val) => Buffer.from(val, 'base64')),
    length: z.number(),
  }),
});

const measurementSetDto = setTemplateDto.extend({
  tmpl: setTemplateDto.nullable(),
});

const bodyMeasurementFieldDto = z.object({
  value: z.number().nullable(),
  isFallback: z.boolean(),
});

const bodyMeasurementDto = z.object({
  weight: z.number().nullable(),
  height: bodyMeasurementFieldDto,
  girth: bodyMeasurementFieldDto,
  hip: bodyMeasurementFieldDto,
  lenThorax: bodyMeasurementFieldDto,
  lenTrunk: bodyMeasurementFieldDto,
  lenArmsUpper: bodyMeasurementFieldDto,
  lenArmsLower: bodyMeasurementFieldDto,
  lenLegsUpper: bodyMeasurementFieldDto,
  lenLegsLower: bodyMeasurementFieldDto,
});

export const measurementDto = z.object({
  bodyMeasurement: bodyMeasurementDto,
  bpress: z.array(
    z.object({
      diastolic: z.number(),
      hf: z.number(),
      map: z.number().nullable(),
      systolic: z.number(),
      time: z.number(),
    }),
  ),
  createDateTime: z.string(),
  diagrams: z.array(
    z.object({
      curves: z.array(
        z.object({
          interval: z.boolean(),
          label: z.string(),
          lineStyle: z.string(),
          pointStyle: z.string(),
          values: z.object({
            checksum: z.number(),
            dataBase64: z
              .string()
              .transform((val) => Buffer.from(val, 'base64')),
            length: z.number(),
          }),
        }),
      ),
      label: z.string(),
      persistent: z.boolean(),
    }),
  ),
  editDateTime: z.string().nullable(),
  filters: z.array(z.string()),
  filtersDegrade: z.boolean(),
  parameters: z.array(
    z.object({
      name: z.string(),
      thresholds: z.array(z.number()),
      persistent: z.boolean(),
      value: z.number(),
    }),
  ),
  records: z.array(
    z.object({
      label: z.string(),
      duration: z.number(),
      calc: z.boolean(),
      exp: z.boolean(),
      hf: z.number().nullable().optional(),
      hfMax: z.number().nullable().optional(),
      hfMin: z.number().nullable().optional(),
      hfStdDev: z.number().nullable().optional(),
      pAxis: z.number().nullable(),
      qrsAxis: z.number().nullable(),
      tAxis: z.number().nullable(),
      beats: z.array(
        z.object({
          flags: z.number(),
          parameters: z.array(
            z.object({
              name: z.string(),
              source: z.string(),
              value: z.number(),
            }),
          ),
          time: z.number(),
          type: z.number(),
        }),
      ),
      parameters: z.array(
        z.object({
          name: z.string(),
          thresholds: z.number().array(),
          persistent: z.boolean(),
          value: z.number(),
        }),
      ),
      events: z.array(
        z.object({
          label: z.string(),
          time: z.number(),
          source: z.string(),
          note: z.string().optional(),
          parameters: z.array(
            z.object({
              name: z.string(),
              value: z.number(),
            }),
          ),
        }),
      ),
      bpress: z.array(
        z.object({
          diastolic: z.number(),
          hf: z.number(),
          map: z.number().nullable(),
          systolic: z.number(),
          time: z.number(),
        }),
      ),
      impedances: z.array(
        z.object({
          frequency: z.number(),
          im: z.number().nullable().optional(),
          re: z.number().nullable().optional(),
          label: z.string(),
          inNeg: z.string(),
          inPos: z.string(),
          outNeg: z.string(),
          outPos: z.string(),
        }),
      ),
      sets: z.array(measurementSetDto),
    }),
  ),
  status: z.number().nonnegative(),
  serLpm: z.number(),
  serMeas: z.number(),
  versHwLpm: z.string(),
  versHwMeas: z.string(),
  versSwLpm: z.string(),
  versSwMeas: z.string(),
});

export type BodyMeasurementFieldDtoType = z.infer<
  typeof bodyMeasurementFieldDto
>;
export type MeasurementDtoType = z.infer<typeof measurementDto>;
